import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { Action, Store } from '@ngrx/store'

import { Observable, of } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { toPayload } from '../../core/helpers/rx-pipes'

import { AccountService } from '../services/account.service'
import * as account from '../actions/account'
import * as auth from '../actions/auth.actions'
import * as status from '../../base/actions/status.actions'
import * as fromAuth from '../reducers'

import { CreateRequest, CreateResponse, ResetRequest } from '../models/account'

@Injectable()
export class AccountEffects {

    constructor(
        private actions$: Actions,
        private store: Store<fromAuth.State>,
        private accountService: AccountService,
        private router: Router
    ) { }

    @Effect()
    startCreate$: Observable<Action> =
        this.actions$
            .pipe(
              ofType<account.Create>(account.CREATE),
                toPayload<CreateRequest>(),
                switchMap(payload =>
                    this.accountService.createAccount(payload)
                        .pipe(
                            map(result => new account.CreateSuccess({...result, password: payload.password})),
                            catchError(e => of(new account.CreateFailed(e)))
                        )
                )
            )

    @Effect({dispatch: false})
    successfulCreation$: Observable<Action | CreateResponse> =
        this.actions$
            .pipe(
              ofType<account.CreateSuccess>((account.CREATE_SUCCESS)),
              toPayload<CreateResponse>(),
              tap((response: CreateResponse) => {
                this.store.dispatch(new status.SetHeader(true))

                if (response.emailVerified && response.validated) {
                  this.store.dispatch(new auth.Login(response))
                } else {
                  this.router.navigate(['create-account', 'success'])
                }
              })
            )

    @Effect()
    startForgot$: Observable<Action> =
        this.actions$.ofType<account.Forgot>(account.FORGOT)
            .pipe(
                toPayload<string>(),
                switchMap(request =>
                    this.accountService.initForgot(request)
                        .pipe(
                            map(result => new account.ForgotSuccess()),
                            catchError(e => of(new account.ForgotFailed(e)))
                        )
                )
            )

    @Effect()
    startReset$: Observable<Action> =
        this.actions$.ofType<account.Reset>(account.RESET)
            .pipe(
                toPayload<ResetRequest>(),
                switchMap(request =>
                    this.accountService.resetPassword(request.token, request.password)
                        .pipe(
                            map(result => new account.ResetSuccess()),
                            catchError(e => of(new account.ResetFailed(e)))
                        )
                )
            )

    @Effect()
    activateAccount: Observable<Action> =
        this.actions$.ofType<account.Activate>(account.ACTIVATE)
            .pipe(
                toPayload<string>(),
                switchMap(token => {
                    return this.accountService.activateAccount(token)
                        .pipe(
                            map(() => new account.ActivateSuccess()),
                            catchError(e => of(new account.ActivateFailed(e)))
                        )
                })
            )

    @Effect()
    validatePasswordResetLink$: Observable<Action> = this.actions$.pipe(
      ofType<account.ValidateResetLink>(account.VALIDATE_RESET_LINK),
      switchMap((action) => {
        return this.accountService.validateResetPassordLink(action.payload)
          .pipe(
            map(linkValid => new account.ValidateResetLinkSuccess(linkValid)),
            catchError(e => of(new account.ValidateResetLinkFailed(e)))
          )
      })
    )

    @Effect()
    userGetLanguage$: Observable<Action> = this.actions$.pipe(
      ofType<account.UserGetLanguage>(account.USER_GET_LANGUAGE),
      switchMap((action) => {
        return this.accountService.getUserLanguage(action.payload)
          .pipe(
            map(language => new account.UserGetLanguageSuccess(language)),
            catchError(e => of(new account.UserGetLanguageFailed(e)))
          )
      })
    )

    @Effect()
    verifyEmailAddress$: Observable<Action> = this.actions$.pipe(
      ofType<account.UserVerifyEmail>(account.USER_VERIFY_EMAIL),
      switchMap((action) => {
        return this.accountService.verifyEmail(action.payload)
          .pipe(
            map((result) => new account.UserVerifyEmailSuccess(result)),
            catchError(e => of(new account.UserVerifyEmailFailed(e)))
          )
      })
    )

    @Effect({ dispatch: false })
    successForgot$: Observable<Action> =
        this.actions$.ofType<account.ForgotSuccess>(account.FORGOT_SUCCESS)
            .pipe(tap(() => this.router.navigate(['reset-account', 'success'])))

    @Effect({ dispatch: false })
    successReset$: Observable<Action> =
        this.actions$.ofType<account.ForgotSuccess>(account.RESET_SUCCESS)
            .pipe(tap(() => this.router.navigate(['reset-password', 'success'])))
}
